import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';
import { Info } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';

import { Answer } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { ExplainerCard } from '@/components/common/ExplainerCard';
import { ExportButton } from '@/components/common/ExportButton';
import { FieldGuide } from '@/components/common/FieldGuide';
import { EmptyState, ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { useAutoLoadMore } from '@/hooks/useAutoLoadMore';
import { formatYearMonth } from '@/lib/datetime';
import { aneelFeeParams, fetchAneelFees } from '@/services/aneel';
import type { AneelFeeExposure, AnswerOut, CategoryShare } from '@/services/types';

const PAGE_SIZE = 50;

/**
 * The TFSEE inspection fee — a levy every concessionaire pays, NOT a penalty.
 *
 * Its own screen for the same reason `/cade/activity` is: it reads a different
 * universe from every other ANEEL page, and the two must never be pooled.
 * 447,701 billing records against 1,569 infraction notices, 3,470 companies
 * against 1,067, and twenty-one years against eight.
 *
 * The fence is the first thing on the page, before any number, because every
 * figure here is dangerous out of context: a "companies" count taken from this
 * screen describes three times as many people as the enforcement screens, and a
 * money total dwarfs every penalty ANEEL has ever imposed.
 *
 * Careful language throughout. A row is "recorded as pending" — never
 * delinquent, and no company is characterised.
 *
 * The credit state is the due-diligence question -- is this company in the
 * debt register, at the Procuradoria, in an instalment plan, on CADIN -- and it
 * is answered at COMPANY grain, filtered by one state or by "anything beyond
 * settled", with the same fence: a collection state of a levy, never a sanction.
 */
export function AneelFeesPage() {
  const { t, i18n } = useTranslation(['aneel', 'common']);
  // Seeded from the URL so the company profile's "fee ledger" link lands on
  // that company's row rather than on page one of 3,470.
  const [seed] = useSearchParams();
  const [draft, setDraft] = useState(seed.get('query') ?? '');
  const [query, setQuery] = useState<string | null>(seed.get('query') || null);
  const [creditStatus, setCreditStatus] = useState(seed.get('credit_status') ?? '');
  const [exposure, setExposure] = useState(seed.get('exposure') === 'true');

  const filters = { query, credit_status: creditStatus || null, exposure };

  const {
    data: paged,
    isPending,
    error,
    refetch,
    isPlaceholderData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['aneel', 'fees', filters],
    queryFn: ({ pageParam }) => fetchAneelFees({ ...filters, limit: PAGE_SIZE, offset: pageParam }),
    initialPageParam: 0,
    // Driven by the server's own total, never by "the last page came back
    // full": a result set that is an exact multiple of PAGE_SIZE would
    // otherwise leave a button promising rows that do not exist.
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((n, page) => n + page.items.length, 0);
      return loaded < lastPage.total ? loaded : undefined;
    },
    placeholderData: keepPreviousData,
  });

  // Every panel above the table reads ledger-level figures that are identical
  // on each page, so they come from the first one. Only the ROWS accumulate.
  const data = paged?.pages[0];
  const items = paged?.pages.flatMap((page) => page.items) ?? [];

  const { sentinelRef, scrollRef, loadMore } = useAutoLoadMore({
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  });

  const number = new Intl.NumberFormat(i18n.language);
  const percent = new Intl.NumberFormat(i18n.language, {
    style: 'percent',
    maximumFractionDigits: 1,
  });
  // Not `.toFixed(1)`: that always renders a period, even in Portuguese, where
  // every other decimal number on this page uses a comma.
  const ratio = new Intl.NumberFormat(i18n.language, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });

  return (
    <div className="space-y-4 p-6">
      <header className="space-y-1">
        <h1 className="text-lg font-semibold text-foreground">{t('aneel:fees.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('aneel:fees.subtitle')}</p>
      </header>

      {/* THE FENCE. First element after the header, before the explainer, before
          the as-of strip, before any number at all. A reader who arrives from an
          enforcement screen and meets a 447,701-row total without this reads it
          as ANEEL's enforcement activity. */}
      <p
        role="alert"
        className="flex items-start gap-2 rounded-md border-2 border-amber-500/50 bg-amber-500/10 p-3 text-sm font-medium text-foreground"
      >
        <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
        <span>{t('aneel:fees.notASanction')}</span>
      </p>

      <ExplainerCard i18nKey="aneel:fees.explainer" />

      <div className="flex justify-end">
        <ExportButton path={`/api/v1/aneel/export/fees?${aneelFeeParams(filters).toString()}`} />
      </div>

      {data ? <AsOfStrip asOf={data.as_of} /> : null}

      {error ? (
        <ErrorMessage message={error.message} onRetry={() => void refetch()} />
      ) : isPending || !data ? (
        <SkeletonLoader rows={8} />
      ) : (
        <div className="space-y-4">
          <section className="rounded-lg border bg-card p-4">
            <h2 className="mb-3 text-sm font-semibold text-foreground">
              {t('aneel:fees.ledger.title')}
            </h2>
            <Answer answer={data.ledger} help={t('aneel:fees.ledger.help')}>
              {(ledger) => (
                <div className="space-y-3">
                  <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                    {(
                      [
                        ['rows', number.format(ledger.rows)],
                        ['entities', number.format(ledger.entities)],
                        ['feeOnly', number.format(ledger.fee_only_entities)],
                        ['span', `${formatYearMonth(ledger.earliest)}–${formatYearMonth(ledger.latest)}`],
                      ] as const
                    ).map(([key, value]) => (
                      <div key={key} className="rounded-md border bg-background p-3">
                        <dt className="text-xs text-muted-foreground">
                          {t(`aneel:fees.ledger.${key}`)}
                        </dt>
                        <dd className="mt-1 text-lg font-semibold tabular-nums text-foreground">
                          {value}
                        </dd>
                      </div>
                    ))}
                  </dl>
                  {/* The population difference, stated as a comparison rather
                      than left for a reader to notice across two screens. */}
                  <p className="rounded-md border bg-muted/40 p-3 text-xs text-muted-foreground">
                    {t('aneel:fees.ledger.population', {
                      entities: number.format(ledger.entities),
                      enforcement: number.format(ledger.enforcement_entities),
                      ratio: ratio.format(ledger.entity_ratio),
                      feeOnly: number.format(ledger.fee_only_entities),
                    })}
                  </p>
                </div>
              )}
            </Answer>
            <div className="mt-3">
              <FieldGuide i18nKey="aneel:fees.guide.ledger" />
            </div>
          </section>

          <div className="grid gap-4 lg:grid-cols-2">
            <MixPanel
              title={t('aneel:fees.collectionMix.title')}
              help={t('aneel:fees.collectionMix.help')}
              shares={data.collection_mix}
            />
            <MixPanel
              title={t('aneel:fees.creditMix.title')}
              help={t('aneel:fees.creditMix.help')}
              shares={data.credit_mix}
            />
          </div>

          <ExposurePanel
            answer={data.exposure}
            onList={(status) => {
              setCreditStatus(status);
              setExposure(false);
            }}
          />

          <section className="rounded-lg border bg-card p-4">
            <h2 className="mb-3 text-sm font-semibold text-foreground">
              {t('aneel:fees.pending.title')}
            </h2>
            <Answer answer={data.pending} help={t('aneel:fees.pending.help')}>
              {(trend) => (
                <div className="overflow-x-auto rounded-md border">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
                      <tr>
                        <th className="px-3 py-2 font-medium">{t('aneel:fees.pending.year')}</th>
                        <th className="px-3 py-2 text-right font-medium">
                          {t('aneel:fees.pending.rows')}
                        </th>
                        <th className="px-3 py-2 text-right font-medium">
                          {t('aneel:fees.pending.pendingRows')}
                        </th>
                        <th className="px-3 py-2 text-right font-medium">
                          {t('aneel:fees.pending.share')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {trend.years.map((row) => (
                        <tr key={row.year} className="border-t">
                          <td className="px-3 py-2 tabular-nums text-foreground">
                            {row.year}
                            {/* The most recent year is marked ON ITS ROW. Its
                                pending share is several times every other
                                year's, and the cause is that its charges have
                                not been reconciled yet. */}
                            {row.year === trend.latest_year ? (
                              <span className="ml-2 rounded border border-amber-500/40 bg-amber-500/10 px-1.5 py-0.5 text-[0.65rem] font-normal">
                                {t('aneel:fees.pending.incomplete')}
                              </span>
                            ) : null}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                            {number.format(row.rows)}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                            {number.format(row.pending)}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums text-foreground">
                            {percent.format(row.pending_share)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Answer>
            <div className="mt-3">
              <FieldGuide i18nKey="aneel:fees.guide.pending" />
            </div>
          </section>

          <form
            className="flex flex-wrap items-end gap-3 rounded-lg border bg-card p-3"
            onSubmit={(event) => {
              event.preventDefault();
              setQuery(draft.trim() || null);
            }}
          >
            <div className="min-w-[16rem] flex-1 space-y-1">
              <label htmlFor="aneel-fee-query" className="text-xs font-medium text-foreground">
                {t('aneel:fees.search.label')}
              </label>
              <input
                id="aneel-fee-query"
                type="search"
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                placeholder={t('aneel:fees.search.placeholder')}
                className="w-full rounded-md border bg-background px-3 py-1.5 text-sm text-foreground"
              />
            </div>
            <button
              type="submit"
              className="rounded-md border bg-foreground px-3 py-1.5 text-sm font-medium text-background"
            >
              {t('aneel:fees.search.submit')}
            </button>
            <div className="space-y-1">
              <label
                htmlFor="aneel-fee-credit-status"
                className="block text-xs font-medium text-foreground"
              >
                {t('aneel:fees.filters.creditStatus')}
              </label>
              {/* Options come from the ledger's own vocabulary, never a literal
                  list: the ETL owns these spellings and a typed one would offer
                  a filter that matches nothing. */}
              <select
                id="aneel-fee-credit-status"
                value={creditStatus}
                onChange={(event) => setCreditStatus(event.target.value)}
                className="max-w-[16rem] rounded-md border bg-background px-2 py-1.5 text-sm text-foreground"
              >
                <option value="">{t('aneel:fees.filters.anyStatus')}</option>
                {data.credit_status_options.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>
            </div>
            <label className="flex items-center gap-2 pb-1.5 text-xs text-foreground">
              <input
                id="aneel-fee-exposure"
                type="checkbox"
                checked={exposure}
                onChange={(event) => setExposure(event.target.checked)}
              />
              {t('aneel:fees.filters.exposure')}
            </label>
          </form>

          {/* Its own element, with the raw total and a staleness flag on it.
              `keepPreviousData` keeps the PREVIOUS query's total on screen for
              the whole of the next fetch, and the rendered sentence gives no
              way to tell which query it describes. */}
          <p
            className="text-sm text-foreground"
            aria-live="polite"
            data-testid="aneel-fees-total"
            data-loaded={items.length}
            data-total={data.total}
            data-stale={String(isPlaceholderData)}
          >
            {t('common:pagination.loaded', {
              loaded: number.format(items.length),
              total: number.format(data.total),
            })}
          </p>

          {items.length === 0 ? (
            <EmptyState hint={t('aneel:fees.emptyHint')} />
          ) : (
            <>
              {/* Bounded height with its own scrollbar, so batches grow inside
                  the box rather than pushing the panels several screens up.
                  The sentinel sits INSIDE it, because that is what scrolls. */}
              <div
                ref={scrollRef}
                className="max-h-[65vh] overflow-auto rounded-lg border"
                role="region"
                aria-label={t('aneel:fees.ledger.title')}
              >
                <table className="w-full text-sm">
                  <thead className="sticky top-0 z-10 bg-muted text-left text-xs text-muted-foreground">
                    <tr>
                      <th className="px-3 py-2 font-medium">{t('aneel:fees.columns.company')}</th>
                      <th className="px-3 py-2 font-medium">{t('aneel:fees.columns.cnpj')}</th>
                      <th className="px-3 py-2 text-right font-medium">
                        {t('aneel:fees.columns.rows')}
                      </th>
                      <th className="px-3 py-2 text-right font-medium">
                        {t('aneel:fees.columns.pending')}
                      </th>
                      <th className="px-3 py-2 font-medium">{t('aneel:fees.columns.credit')}</th>
                      <th className="px-3 py-2 font-medium">{t('aneel:fees.columns.span')}</th>
                      <th className="px-3 py-2 font-medium">
                        {t('aneel:fees.columns.enforcement')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((row) => (
                      <tr key={`${row.cnpj}-${row.name}`} className="border-t align-top">
                        {/* The only route to the 2,375 companies that appear
                            in nothing but this ledger. Their profile is where
                            the fee/sanction fence gets restated. */}
                        <td className="px-3 py-2 text-foreground">
                          {row.cnpj && row.cnpj.length === 14 ? (
                            <Link
                              to={`/aneel/e/${row.cnpj}`}
                              className="text-primary underline-offset-2 hover:underline"
                            >
                              {row.name ?? row.cnpj}
                            </Link>
                          ) : (
                            (row.name ?? '—')
                          )}
                        </td>
                        <td className="px-3 py-2 tabular-nums text-muted-foreground">
                          {row.cnpj ?? '—'}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                          {number.format(row.rows)}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-foreground">
                          {number.format(row.pending_rows)}
                        </td>
                        {/* Rows per credit state other than settled: how the
                            LEVY was collected. Most companies have none, and
                            that reads as "nothing beyond settled", never as a
                            clean record of anything else. */}
                        <td className="px-3 py-2 text-xs text-muted-foreground" data-credit-states>
                          {row.credit_statuses.length === 0 ? (
                            t('aneel:fees.columns.creditNone')
                          ) : (
                            <ul className="flex flex-wrap gap-1">
                              {row.credit_statuses.map((state) => (
                                <li
                                  key={state.status}
                                  className="rounded border px-1.5 py-0.5 tabular-nums"
                                  data-credit-state={state.status}
                                >
                                  {state.status} · {number.format(state.rows)}
                                </li>
                              ))}
                            </ul>
                          )}
                        </td>
                        <td className="px-3 py-2 tabular-nums text-muted-foreground">
                          {formatYearMonth(row.earliest)}–{formatYearMonth(row.latest)}
                        </td>
                        {/* Stated per company so a long fee ledger is never
                            read as an enforcement history. Most companies here
                            have no infraction notice at all. */}
                        <td className="px-3 py-2 text-muted-foreground">
                          {row.has_enforcement
                            ? t('aneel:fees.columns.hasEnforcement')
                            : t('aneel:fees.columns.noEnforcement')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <div ref={sentinelRef} aria-hidden className="h-px" />

                <div className="flex justify-center py-3">
                  {hasNextPage ? (
                    <button
                      type="button"
                      onClick={loadMore}
                      disabled={isFetchingNextPage}
                      className="rounded-md border bg-background px-3 py-1.5 text-sm text-foreground disabled:opacity-40"
                    >
                      {isFetchingNextPage
                        ? t('common:pagination.loadingMore')
                        : t('common:pagination.loadMore', { count: PAGE_SIZE })}
                    </button>
                  ) : (
                    <p className="text-xs text-muted-foreground">
                      {t('common:pagination.allLoaded', { total: number.format(data.total) })}
                    </p>
                  )}
                </div>
              </div>

              <FieldGuide i18nKey="aneel:fees.guide.columns" />
            </>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Companies per credit state other than settled -- the due-diligence question,
 * at company grain. The credit-mix panel beside it counts ROWS, and 1,082 rows
 * in the debt register say nothing about how many companies that is.
 *
 * Each state's button narrows the list below to those companies, so the count
 * on the row and the total under the filter are the same predicate.
 */
function ExposurePanel({
  answer,
  onList,
}: {
  answer: AnswerOut<AneelFeeExposure>;
  onList: (status: string) => void;
}) {
  const { t, i18n } = useTranslation(['aneel']);
  const number = new Intl.NumberFormat(i18n.language);
  const percent = new Intl.NumberFormat(i18n.language, {
    style: 'percent',
    maximumFractionDigits: 1,
  });

  return (
    <section className="rounded-lg border bg-card p-4" data-fee-exposure>
      <h2 className="mb-3 text-sm font-semibold text-foreground">
        {t('aneel:fees.exposure.title')}
      </h2>
      <Answer answer={answer} help={t('aneel:fees.exposure.help')}>
        {(exposure) => (
          <div className="space-y-3">
            <p
              className="rounded-md border bg-muted/40 p-3 text-xs text-muted-foreground"
              data-exposure-headline
              data-exposed={exposure.entities_with_exposure}
            >
              {t('aneel:fees.exposure.headline', {
                exposed: number.format(exposure.entities_with_exposure),
                entities: number.format(exposure.entities),
                share: percent.format(exposure.exposure_share),
              })}
            </p>
            {exposure.statuses.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('aneel:fees.exposure.none')}</p>
            ) : (
              <div className="overflow-x-auto rounded-md border">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
                    <tr>
                      <th className="px-3 py-2 font-medium">
                        {t('aneel:fees.exposure.columns.status')}
                      </th>
                      <th className="px-3 py-2 text-right font-medium">
                        {t('aneel:fees.exposure.columns.rows')}
                      </th>
                      <th className="px-3 py-2 text-right font-medium">
                        {t('aneel:fees.exposure.columns.entities')}
                      </th>
                      <th className="px-3 py-2 text-right font-medium">
                        {t('aneel:fees.exposure.columns.share')}
                      </th>
                      <th className="px-3 py-2 font-medium">
                        <span className="sr-only">{t('aneel:fees.exposure.columns.list')}</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {exposure.statuses.map((row) => (
                      <tr
                        key={row.status}
                        className="border-t"
                        data-exposure-status={row.status}
                        data-entities={row.entities}
                      >
                        <td className="px-3 py-2 text-foreground">{row.status}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                          {number.format(row.rows)}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-foreground">
                          {number.format(row.entities)}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                          {percent.format(row.entity_share)}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <button
                            type="button"
                            onClick={() => onList(row.status)}
                            aria-label={t('aneel:fees.exposure.listLabel', { status: row.status })}
                            className="text-xs text-primary underline-offset-2 hover:underline"
                          >
                            {t('aneel:fees.exposure.columns.list')}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </Answer>
      <div className="mt-3">
        <FieldGuide i18nKey="aneel:fees.guide.exposure" />
      </div>
    </section>
  );
}

function MixPanel({
  title,
  help,
  shares,
}: {
  title: string;
  help: string;
  shares: CategoryShare[];
}) {
  const { t, i18n } = useTranslation(['aneel']);
  const number = new Intl.NumberFormat(i18n.language);
  const percent = new Intl.NumberFormat(i18n.language, {
    style: 'percent',
    maximumFractionDigits: 1,
  });

  return (
    <section className="rounded-lg border bg-card p-4">
      <h2 className="mb-1 text-sm font-semibold text-foreground">{title}</h2>
      <p className="mb-3 text-xs text-muted-foreground">{help}</p>
      <ul className="space-y-1">
        {shares.map((share) => (
          <li
            key={share.code}
            className="flex items-baseline justify-between gap-3 border-b pb-1 text-sm last:border-b-0"
          >
            {/* `(none)` is the SQL coalesce placeholder for a null status. It
                reached the screen as a literal, which reads as a category ANEEL
                uses rather than as an absent value — and does so in English on
                a Portuguese page. */}
            <span className="text-foreground">
              {share.code === '(none)' ? t('aneel:fees.notRecorded') : share.code}
            </span>
            <span className="tabular-nums text-muted-foreground">
              {number.format(share.count)} · {percent.format(share.share)}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}
